import { useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { ChatActionIcon as AgentIcon } from '@erp/ui/chat-action-icon';

interface ChatInputActionsProps {
  isLoading: boolean;
  /** A reply streams and the composer queues behind it (AI-518). */
  queueing: boolean;
  canSend: boolean;
  onSend: () => void;
  onCancel?: () => void;
}

/** The composer's buttons: Send, or Stop while replying, or both when queueing. */
export function ChatInputActions({
  isLoading,
  queueing,
  canSend,
  onSend,
  onCancel,
}: ChatInputActionsProps) {
  const gt = useGT();
  const send = (
    <Button
      onClick={onSend}
      disabled={!canSend}
      size="sm"
      className="h-7 px-2 send-hover chat-send-button"
      aria-label={
        queueing
          ? gt('Queue message', {
              context:
                'chat send button while the assistant is replying; the message runs after the reply',
            })
          : gt('Send message', { context: 'send chat message button' })
      }
    >
      <AgentIcon state="send" size={20} />
    </Button>
  );
  if (!isLoading) return send;
  const stop = (
    <Button
      onClick={onCancel}
      disabled={!onCancel}
      variant="secondary"
      size="sm"
      className="h-7 px-2 send-hover"
      aria-label={
        onCancel
          ? gt('Cancel', { context: 'cancel AI response button' })
          : undefined
      }
    >
      <AgentIcon state="thinking" size={20} />
    </Button>
  );
  if (!queueing) return stop;
  return (
    <div className="flex items-center gap-1">
      {stop}
      {send}
    </div>
  );
}

/** Explains Enter and Ctrl/⌘+Enter while a reply streams. */
export function QueueHint({ canSteer }: { canSteer: boolean }) {
  const gt = useGT();
  return (
    <p className="mt-1 text-xs text-muted-foreground">
      {canSteer
        ? gt(
            'Enter queues your message for after this reply · Ctrl/⌘+Enter adds it after the current step',
            {
              context:
                'hint under the chat input while the assistant is replying',
            },
          )
        : gt('Enter queues your message for after this reply', {
            context:
              'hint under the chat input while the assistant is replying',
          })}
    </p>
  );
}
